import type { JobTag } from '@seek/chalice-types';
import type { Locale } from '@seek/melways-sites';

import {
  RECOMMENDED_JOB_AI_VIEW_ORIGIN,
  RECOMMENDED_JOB_VIEW_ORIGIN,
} from 'src/components/HomePage/Dashboard/SignedInDashboard/DashboardRecommendations/RecommendedJobCard';
import { getRecommendationBadges } from 'src/components/HomePage/Dashboard/SignedInDashboard/SharedComponent/JobCard/hooks/getRecommendationBadges';
import { mapJobBadgesToJobTags } from 'src/components/HomePage/Dashboard/SignedInDashboard/hooks/feed-types';
import type {
  CareerFeedJobBadgesFragmentFragment,
  RecommendedGlobalJobTagsFragmentFragment,
} from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';
import type { ApolloClientType } from 'src/types/apollo';

import {
  CareerFeedJobBadgesFragment,
  RecommendedGlobalJobTagsFragment,
} from './RecommendedJobAnalyticsBadgesFragments.graphql';

const HOME_RECS_ALLOWED_ORIGIN = [
  RECOMMENDED_JOB_VIEW_ORIGIN,
  RECOMMENDED_JOB_AI_VIEW_ORIGIN,
];
const shouldSkipRead = (origin?: string) =>
  Boolean(origin && !HOME_RECS_ALLOWED_ORIGIN.includes(origin));

const getJobTagsFromRecommendedGlobalJob = ({
  apolloClient,
  jobId,
  locale,
}: Pick<
  GetRecommendedBadgesForTrackingProps,
  'apolloClient' | 'jobId' | 'locale'
>) => {
  try {
    const recommendedJob =
      apolloClient.cache.readFragment<RecommendedGlobalJobTagsFragmentFragment>(
        {
          id: `RecommendedGlobalJob:${jobId}`,
          fragment: RecommendedGlobalJobTagsFragment,
          variables: {
            locale,
          },
        },
      );

    return recommendedJob?.tags as JobTag[] | undefined;
  } catch (e) {
    if (e instanceof Error) {
      logger.error(
        { err: e, input: { id: jobId } },
        'Failed to build tracking data for recommended job badges',
      );
    }
    return [];
  }
};

const getJobTagsFromCareerFeedJob = ({
  apolloClient,
  jobId,
  visitorId,
  locale,
}: Pick<
  GetRecommendedBadgesForTrackingProps,
  'apolloClient' | 'jobId' | 'visitorId' | 'locale'
>) => {
  if (!jobId || !visitorId) {
    return [];
  }

  try {
    const targetJob = apolloClient.cache.identify({
      jobId,
      __typename: 'CareerFeedJob',
    });

    if (!targetJob) {
      return [];
    }

    const careerFeedJob =
      apolloClient.cache.readFragment<CareerFeedJobBadgesFragmentFragment>({
        id: targetJob,
        fragment: CareerFeedJobBadgesFragment,
        variables: {
          visitorId,
          locale,
        },
      });

    return mapJobBadgesToJobTags(careerFeedJob?.jobBadges);
  } catch (e) {
    if (e instanceof Error) {
      logger.error(
        { err: e, input: { id: jobId } },
        'Failed to build tracking data for recommended job badges',
      );
    }
    return [];
  }
};

interface GetRecommendedBadgesForTrackingProps {
  jobId: string;
  apolloClient: ApolloClientType;
  origin?: string;
  visitorId: string;
  locale: Locale;
  features?: {
    isFeedEnabled?: boolean;
    isHomepageRecsBadgingStrongApplicantEnabled?: boolean;
  };
}
export const getRecommendedBadgesForTracking = ({
  jobId,
  apolloClient,
  origin,
  visitorId,
  locale,
  features: { isFeedEnabled, isHomepageRecsBadgingStrongApplicantEnabled } = {},
}: GetRecommendedBadgesForTrackingProps) => {
  if (shouldSkipRead(origin)) {
    return [];
  }

  const tags = isFeedEnabled
    ? getJobTagsFromCareerFeedJob({
        apolloClient,
        jobId,
        visitorId,
        locale,
      })
    : getJobTagsFromRecommendedGlobalJob({
        apolloClient,
        jobId,
        locale,
      });

  const { analyticsBadges } = getRecommendationBadges(
    tags,
    isHomepageRecsBadgingStrongApplicantEnabled,
  );

  return analyticsBadges;
};
